import { inject, injectable } from "inversify";
import React from "react";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { UserStore } from "../../../../services/UserStore";
import { UrlStore } from "../../../../services/urlStore";
import { FiltersStore } from "../../../../services/filtersStore";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { deleteJSON, getJSON, postJSON } from "@whatslly/commons/apiUtils";
import { modalService } from "@whatslly/ui-components/modals";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { TFunction } from "i18next";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import { loginListener } from "@whatslly/commons/loginListener";
import { LoginCommands } from "@whatslly/commons/loginCommands";
import { parseOrReturnError } from "../../../../services/utils";
import type { ArchivePlatformListResponse } from "@whatslly/types/archive-platforms/ArchiveApiSchemas";
import type { DiscordArchivePlatformRecord } from "@whatslly/types/archive-platforms/ArchivePlatforms";
@injectable()
export class ArchiveDiscordSettingsStore {
  isInitialized = false;
  connected = false;
  isConnecting = false;
  platforms?: DiscordArchivePlatformRecord[];

  constructor(
    @inject(InjectUserStore) private readonly userStore: UserStore,
    @inject(FiltersStore) private readonly filtersStore: FiltersStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    void this.urlStore;
    makeObservable(this, {
      isInitialized: observable,
      connected: observable,
      isConnecting: observable,
      platforms: observable,
      isDiscordArchiveEnabled: computed,
      isReadOnlySettingsUser: computed,
      orgId: computed,
      init: action.bound,
    });
    this.init();
  }

  get isDiscordArchiveEnabled() {
    return Boolean(this.userStore.user?.allow_archive);
  }

  get isReadOnlySettingsUser() {
    return !this.isDiscordArchiveEnabled || !hasPermission(this.userStore.user, UserPermissions["archive.update_settings"]);
  }

  get orgId() {
    return this.filtersStore.orgId || this.userStore.user?.organizationId;
  }

  async init() {
    if (!this.isDiscordArchiveEnabled || !hasPermission(this.userStore.user, UserPermissions["archive.view_settings"])) {
      runInAction(() => {
        this.connected = false;
        this.isInitialized = true;
      });
      return;
    }

    try {
      const res = await getJSON({
        urlSuffix: `/archive/${this.orgId}`,
        pod: this.userStore.pod,
        etoken: this.userStore.etoken,
      }).then((r) => parseOrReturnError<ArchivePlatformListResponse<DiscordArchivePlatformRecord>>(r, this.logger));

      runInAction(() => {
        if (res.success && Array.isArray(res.platforms)) {
          this.connected = res.platforms.length > 0;
          this.platforms = this.connected ? res.platforms : undefined;
        } else {
          this.connected = false;
          this.platforms = undefined;
        }
        this.isInitialized = true;
      });
    } catch (err) {
      this.logger.error({ err }, "Failed to check Archive Discord connection status");
      runInAction(() => {
        this.connected = false;
        this.platforms = undefined;
        this.isInitialized = true;
      });
    }
  }

  async connectArchiveDiscord(t: TFunction) {
    runInAction(() => {
      this.isConnecting = true;
    });

    try {
      const redirectRes = await getJSON({
        urlSuffix: `/archive/discord/redirect?${new URLSearchParams({ orgId: this.orgId ?? "" }).toString()}`,
        pod: this.userStore.pod,
        etoken: this.userStore.etoken ?? "",
      }).then((response) => response.json() as Promise<{ success: boolean; url?: string; error?: string }>);

      if (!redirectRes.success || !redirectRes.url) {
        throw new Error(redirectRes.error || t("archive_discord_settings_page.connection_failed"));
      }

      const oauthData = await this.waitForDiscordOauthCompletion(redirectRes.url, t);
      if (oauthData.error) {
        throw new Error(oauthData.error_description || oauthData.error || t("archive_discord_settings_page.connection_failed"));
      }
      if (!oauthData.code || !oauthData.state) {
        throw new Error(t("archive_discord_settings_page.connection_failed"));
      }

      const res = await postJSON({
        urlSuffix: `/archive/${this.orgId}/discord/oauth-connect`,
        pod: this.userStore.pod,
        etoken: this.userStore.etoken ?? "",
        body: JSON.stringify({
          code: oauthData.code,
          state: oauthData.state,
        }),
      }).then((response) => response.json());

      if (!res.success) {
        throw new Error(res.error || t("archive_discord_settings_page.connection_failed"));
      }

      runInAction(() => {
        this.isConnecting = false;
      });
      modalService.openModal(Toast, { text: t("archive_discord_settings_page.connected_success"), type: "regular" });
      this.init();
    } catch (err: unknown) {
      runInAction(() => {
        this.isConnecting = false;
      });
      const message = err instanceof Error ? err.message : t("archive_discord_settings_page.connection_failed");
      modalService.openModal(Toast, { text: message, type: "error" });
    }
  }

  async openServerSelectionWindow(t: TFunction) {
    runInAction(() => {
      this.isConnecting = true;
    });

    try {
      const redirectRes = await getJSON({
        urlSuffix: `/archive/discord/redirect?${new URLSearchParams({ orgId: this.orgId ?? "" }).toString()}`,
        pod: this.userStore.pod,
        etoken: this.userStore.etoken ?? "",
      }).then((response) => response.json() as Promise<{ success: boolean; url?: string; error?: string }>);

      if (!redirectRes.success || !redirectRes.url) {
        throw new Error(redirectRes.error || t("archive_discord_settings_page.connection_failed"));
      }

      const oauthData = await this.waitForDiscordOauthCompletion(redirectRes.url, t);
      if (oauthData.error) {
        throw new Error(oauthData.error_description || oauthData.error || t("archive_discord_settings_page.connection_failed"));
      }

      modalService.openModal(Toast, {
        text: t("archive_discord_settings_page.connected_success"),
        type: "regular",
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("archive_discord_settings_page.connection_failed");
      modalService.openModal(Toast, { text: message, type: "error" });
    } finally {
      runInAction(() => {
        this.isConnecting = false;
      });
    }
  }

  async disconnect(platformId: string) {
    await deleteJSON({
      urlSuffix: `/archive/${this.orgId}/${platformId}`,
      etoken: this.userStore.etoken ?? "",
      pod: this.userStore.pod,
      body: JSON.stringify({}),
    });

    this.init();
  }

  async openUninstallModal(platformId: string, t: TFunction) {
    const modal = modalService.openModal(ConfirmModal, {
      modalType: "warning",
      title: t("archive_discord_settings_page.uninstall_modal.title"),
      body: (
        <>
          <p>{t("archive_discord_settings_page.uninstall_modal.description")}</p>
          <p style={{ fontWeight: 600 }}>{t("archive_discord_settings_page.uninstall_modal.warning")}</p>
        </>
      ),
      okText: t("archive_discord_settings_page.uninstall_modal.confirm"),
      cancelText: t("archive_discord_settings_page.uninstall_modal.cancel"),
      testId: "uninstall-archive-discord-modal",
    });
    const result = await modal.promise;

    if (result?.result) {
      try {
        await this.disconnect(platformId);
        modalService.openModal(Toast, {
          text: t("archive_discord_settings_page.uninstall_modal.success"),
          type: "regular",
        });
      } catch {
        modalService.openModal(Toast, {
          text: t("archive_discord_settings_page.uninstall_modal.error"),
          type: "error",
        });
      }
    }
  }

  private async waitForDiscordOauthCompletion(url: string, t: TFunction) {
    const top = (window.screen.availHeight - 600) / 2;
    const left = (window.screen.availWidth - 700) / 2;
    const options: Record<string, number | boolean> = {
      width: 700,
      height: 600,
      top: top > 0 ? top : 0,
      left: left > 0 ? left : 0,
      location: true,
      resizable: true,
      statusbar: true,
      toolbar: false,
    };
    const winOpt = Object.keys(options)
      .map((op) => `${op}=${Number(options[op])}`)
      .join(",");
    const popup = window.open(url, "Discord OAuth", winOpt);
    if (!popup) {
      throw new Error(t("slack_settings_page.popup_blocked"));
    }

    return await new Promise<{
      code?: string;
      state?: string;
      appName?: string;
      error?: string;
      error_description?: string;
    }>((resolve, reject) => {
      const unsubscribe = loginListener.on(LoginCommands.LOGIN_COMPLETED, (data) => {
        if (data.appName !== "discord-archive") {
          return;
        }
        clearInterval(interval);
        resolve(data);
        unsubscribe();
      });
      const interval = setInterval(() => {
        if (popup.closed) {
          clearInterval(interval);
          unsubscribe();
          reject(new Error(t("archive_discord_settings_page.connection_failed")));
        }
      }, 1000);
    });
  }
}
